/**
 * @fileoverview Announcements Hook
 * 
 * Fetches active announcements and handles dismissals.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface Announcement {
  id: string;
  title: string;
  message: string | null;
  link_url: string | null;
  link_text: string | null;
  bg_color: string;
  is_dismissible: boolean;
  starts_at: string;
  ends_at: string | null;
}

export function useAnnouncements() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Get dismissed IDs from localStorage for non-logged-in users
  const getLocalDismissed = (): string[] => {
    try {
      return JSON.parse(localStorage.getItem('dismissed_announcements') || '[]');
    } catch {
      return [];
    }
  };

  const { data: announcements = [], isLoading } = useQuery({
    queryKey: ['announcements', user?.id],
    queryFn: async () => {
      // Fetch active announcements
      const { data: announcementsData, error: announcementsError } = await (supabase
        .from('announcements' as any)
        .select('*')
        .order('created_at', { ascending: false }) as any);

      if (announcementsError) throw announcementsError;

      // Fetch dismissed announcements for logged-in users
      let dismissedIds: string[] = getLocalDismissed();
      
      if (user) {
        const { data: dismissedData } = await (supabase
          .from('dismissed_announcements' as any)
          .select('announcement_id')
          .eq('user_id', user.id) as any);
        
        if (dismissedData) {
          dismissedIds = [
            ...dismissedIds,
            ...dismissedData.map((d: any) => d.announcement_id),
          ];
        }
      }

      // Filter out dismissed announcements
      return (announcementsData as Announcement[]).filter(
        (a) => !dismissedIds.includes(a.id)
      );
    },
    staleTime: 60000, // 1 minute
  });

  const dismissMutation = useMutation({
    mutationFn: async (announcementId: string) => {
      if (user) {
        // Save to database for logged-in users
        await (supabase
          .from('dismissed_announcements' as any)
          .insert({ user_id: user.id, announcement_id: announcementId }) as any);
      } else {
        // Save to localStorage for anonymous users
        const dismissed = getLocalDismissed();
        if (!dismissed.includes(announcementId)) {
          localStorage.setItem(
            'dismissed_announcements',
            JSON.stringify([...dismissed, announcementId])
          );
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });

  return {
    announcements,
    isLoading,
    dismiss: dismissMutation.mutate,
  };
}
